import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  InputAdornment,
  Typography,
  Divider,
} from '@mui/material';
import { Employee, EmployeeRole, EMPLOYEE_ROLES } from '../../types';

const getEmptyEmployee = (): Omit<Employee, 'id'> => ({
  name: '',
  email: '',
  phone: '',
  role: 'vendedor',
  base_salary: 0,
  target_bonus: undefined,
  monthly_sales_target: undefined,
  target_in_units: true,
  admission_date: '',
  notes: '',
  active: true,
});

export interface EmployeeFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (employee: Employee) => void;
  /** Quando undefined/null = modo criação; senão = edição */
  employee?: Employee | null;
}

const EmployeeFormModal: React.FC<EmployeeFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  employee = null,
}) => {
  const isEdit = Boolean(employee?.id);

  const [form, setForm] = useState<Omit<Employee, 'id'> & { id?: number }>(() => ({
    ...getEmptyEmployee(),
    id: undefined,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = useCallback(() => {
    if (employee) {
      setForm({
        id: employee.id,
        name: employee.name ?? '',
        email: employee.email ?? '',
        phone: employee.phone ?? '',
        role: employee.role,
        base_salary: employee.base_salary ?? 0,
        target_bonus: employee.target_bonus ?? undefined,
        monthly_sales_target: employee.monthly_sales_target ?? undefined,
        target_in_units: employee.target_in_units ?? true,
        admission_date: employee.admission_date ?? '',
        notes: employee.notes ?? '',
        active: employee.active ?? true,
      });
    } else {
      setForm({ ...getEmptyEmployee(), id: undefined });
    }
    setErrors({});
  }, [employee]);

  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  const showCommissionFields = form.role === 'vendedor' || form.role === 'gerente';

const validate = (): boolean => {
  const next: Record<string, string> = {};

  if (!form.name?.trim()) {
    next.name = 'Nome é obrigatório';
  }

  if (!form.email?.trim()) {
    next.email = 'E-mail é obrigatório';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    next.email = 'E-mail inválido';
  }

  if (!form.role) {
    next.role = 'Cargo é obrigatório';
  }

  if (form.base_salary == null || form.base_salary === 0) {
    next.base_salary = 'Salário é obrigatório';
  } else if (Number(form.base_salary) < 0) {
    next.base_salary = 'Salário não pode ser negativo';
  }

  if (!form.admission_date) {
    next.admission_date = 'Data de admissão é obrigatória';
  }

  if (form.monthly_sales_target != null && Number(form.monthly_sales_target) < 0) {
    next.monthly_sales_target = 'Meta deve ser positiva';
  }

  if (form.target_bonus != null && Number(form.target_bonus) < 0) {
    next.target_bonus = 'Bônus não pode ser negativo';
  }

  setErrors(next);
  return Object.keys(next).length === 0;
};
  const handleChange = (field: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
    setForm((prev) => ({ ...prev, base_salary: v }));
    if (errors.base_salary) setErrors((prev) => ({ ...prev, base_salary: '' }));
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload: Employee = {
      id: isEdit ? (employee!.id) : 0,
      name: form.name.trim(),
      email: form.email?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      role: form.role as EmployeeRole,
      base_salary: Number(form.base_salary) || 0,
      target_bonus: showCommissionFields && form.target_bonus != null ? form.target_bonus : undefined,
      monthly_sales_target: showCommissionFields && form.monthly_sales_target != null ? form.monthly_sales_target : undefined,
      target_in_units: form.target_in_units ?? true,
      admission_date: form.admission_date || undefined,
      notes: form.notes?.trim() || undefined,
      active: form.active ?? true,
    };
    onSubmit(payload);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        },
      }}
      aria-labelledby="employee-form-dialog-title"
    >
      <DialogTitle id="employee-form-dialog-title">
        {isEdit ? 'Editar funcionário' : 'Novo funcionário'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Nome completo"
              value={form.name}
              onChange={handleChange('name')}
              error={Boolean(errors.name)}
              helperText={errors.name}
              autoFocus
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={form.email ?? ''}
              onChange={handleChange('email')}
              error={Boolean(errors.email)}
              helperText={errors.email}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Telefone"
              value={form.phone ?? ''}
              onChange={handleChange('phone')}
              placeholder="(11) 98765-4321"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
              <InputLabel>Cargo</InputLabel>
              <Select<EmployeeRole>
                label="Cargo"
                value={form.role}
                onChange={(e: SelectChangeEvent<EmployeeRole>) => {
                  setForm((prev) => ({ ...prev, role: e.target.value as EmployeeRole }));
                  if (errors.role) setErrors((prev) => ({ ...prev, role: '' }));
                }}
              >
                {(Object.entries(EMPLOYEE_ROLES) as [EmployeeRole, string][]).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Salário base (R$)"
              type="number"
              value={form.base_salary || ''}
              onChange={handleSalaryChange}
              error={Boolean(errors.base_salary)}
              helperText={errors.base_salary}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { min: 0, step: 50 },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.active ?? true}
                  onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                  color="primary"
                />
              }
              label="Ativo"
            />
          </Grid>

          {showCommissionFields && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Metas
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Bônus por meta (R$)"
                  type="number"
                  value={form.target_bonus ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                    setForm((prev) => ({ ...prev, target_bonus: v }));
                    if (errors.target_bonus) setErrors((prev) => ({ ...prev, target_bonus: '' }));
                  }}
                  error={Boolean(errors.target_bonus)}
                  helperText={errors.target_bonus}
                  inputProps={{ min: 0 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Meta mensal"
                  type="number"
                  value={form.monthly_sales_target ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? undefined : parseFloat(e.target.value);
                    setForm((prev) => ({ ...prev, monthly_sales_target: v }));
                    if (errors.monthly_sales_target) setErrors((prev) => ({ ...prev, monthly_sales_target: '' }));
                  }}
                  error={Boolean(errors.monthly_sales_target)}
                  helperText={errors.monthly_sales_target}
                  inputProps={{ min: 0 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <InputLabel>Tipo de meta</InputLabel>
                  <Select
                    label="Tipo de meta"
                    value={form.target_in_units ? 'units' : 'value'}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, target_in_units: e.target.value === 'units' }))
                    }
                  >
                    <MenuItem value="units">Quantidade (veículos)</MenuItem>
                    <MenuItem value="value">Valor (R$)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Data de admissão"
              type="date"
              value={form.admission_date ?? ''}
              onChange={handleChange('admission_date')}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Observações"
              value={form.notes ?? ''}
              onChange={handleChange('notes')}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" sx={{ borderRadius: '8px' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ borderRadius: '8px' }}
        >
          {isEdit ? 'Salvar' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeFormModal;
